import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, ArrowLeft, Trash2, Search, Globe, Linkedin } from "lucide-react";
import { createDeal, editDealInfo } from "@/api/deals-api";
import { companySearchApi } from "@/api/company-search-api";
import { useWorkspaces } from "@/store/workspace-store";
import { useUser } from "@clerk/clerk-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import posthog from "posthog-js";
import { getUrlValidationError, getLinkedInUrlValidationError } from "@/lib/url-utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { useDealsListStore } from "@/store/deals-list-store";
import { getErrorMessage } from "@/lib/api-error";

interface CompanySearchResult {
  source: "web" | "linkedin";
  company_name?: string;
  website_url?: string;
  linkedin_url?: string;
  description?: string;
  favicon?: string;
}

interface DealRow {
  id: string;
  name: string;
  website: string;
  linkedin: string;
  stage: string;
  source: string;
}

const BATCH_SIZE = 5;

export function BulkAddDealsPage() {
  const { navigate } = useWorkspaceNavigation();
  const { activeWorkspaceId, activeWorkspace } = useWorkspaces();
  const { user } = useUser();
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchDeals } = useDealsListStore();
  const [deals, setDeals] = useState<DealRow[]>([
    { id: "1", name: "", website: "", linkedin: "", stage: "", source: "Manual" },
  ]);
  const [websiteErrors, setWebsiteErrors] = useState<Record<string, string>>({});
  const [linkedinErrors, setLinkedinErrors] = useState<Record<string, string>>({});

  // Company search state
  const [searchingDealId, setSearchingDealId] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  const [searchPopoverOpenId, setSearchPopoverOpenId] = useState<string | null>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!searchPopoverOpenId) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-search-dropdown]")) {
        setSearchPopoverOpenId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchPopoverOpenId]);

  const sourceOptions = [
    "Submission",
    "Email",
    "Intro",
    "Referral",
    "CoInvestor",
    "Angel",
    "LP",
    "Accelerator",
    "Conference",
    "Platform",
    "Advisor",
    "TechTransfer",
    "Grant",
    "Social",
    "Media",
    "Manual",
  ];

  const stageOptions =
    activeWorkspace?.statuses.map((status) => status.title) || [];

  const addNewRow = useCallback(() => {
    setDeals((prev) => [
      ...prev,
      {
        id: (prev.length + 1).toString(),
        name: "",
        website: "",
        linkedin: "",
        stage: "",
        source: "Manual",
      },
    ]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setDeals((prev) => prev.filter((deal) => deal.id !== id));
    setWebsiteErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLinkedinErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updateDeal = useCallback(
    (id: string, field: keyof DealRow, value: string) => {
      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === id ? { ...deal, [field]: value } : deal
        )
      );
      if (field === "website") {
        setWebsiteErrors((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
      if (field === "linkedin") {
        setLinkedinErrors((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    []
  );

  // Company search handler
  const handleCompanySearch = useCallback(async (dealId: string, companyName: string) => {
    if (!companyName.trim()) {
      toast.error("Please enter a company name to search");
      return;
    }

    setSearchingDealId(dealId);
    setSearchResults([]);
    setSearchPopoverOpenId(dealId);

    try {
      const [webResponse, linkedInResponse] = await Promise.allSettled([
        companySearchApi.searchWeb(companyName),
        companySearchApi.searchLinkedIn(companyName),
      ]);

      // Extract data from both responses first
      let websiteUrl: string | undefined;
      let fullLinkedInUrl: string | undefined;
      let companyTitle: string | undefined;
      let description: string | undefined;
      let webFavicon: string | undefined;
      let linkedInFavicon: string | undefined;

      // Process web search results - get website URL
      if (webResponse.status === "fulfilled" && webResponse.value?.data) {
        const responseData = webResponse.value.data as { results?: Array<{ url?: string; title?: string; description?: string; favicon?: string }> };
        if (responseData.results && Array.isArray(responseData.results)) {
          const websiteResult = responseData.results.find(r => !r.url?.includes('linkedin.com'));
          if (websiteResult) {
            websiteUrl = websiteResult.url;
            companyTitle = websiteResult.title?.split(' | ')[0]?.split(' - ')[0]?.trim();
            description = websiteResult.description;
            webFavicon = websiteResult.favicon;
          }
        }
      }

      // Process LinkedIn search results - get full LinkedIn URL
      if (linkedInResponse.status === "fulfilled" && linkedInResponse.value?.data) {
        const responseData = linkedInResponse.value.data as { results?: Array<{ url?: string; title?: string; description?: string; favicon?: string }> };
        if (responseData.results && Array.isArray(responseData.results)) {
          const linkedInResult = responseData.results.find(r =>
            r.url?.includes('linkedin.com/company/')
          ) || responseData.results.find(r => r.url?.includes('linkedin.com'));

          if (linkedInResult) {
            fullLinkedInUrl = linkedInResult.url;
            linkedInFavicon = linkedInResult.favicon;
            if (!companyTitle) {
              companyTitle = linkedInResult.title?.split(' | ')[0]?.trim();
            }
            if (!description) {
              description = linkedInResult.description;
            }
          }
        }
      }

      // Build results
      const results: CompanySearchResult[] = [];

      if (websiteUrl) {
        results.push({
          source: "web",
          company_name: companyTitle,
          website_url: websiteUrl,
          linkedin_url: fullLinkedInUrl,
          description: description,
          favicon: webFavicon,
        });
      }

      if (fullLinkedInUrl) {
        results.push({
          source: "linkedin",
          company_name: companyTitle,
          website_url: websiteUrl,
          linkedin_url: fullLinkedInUrl,
          description: description,
          favicon: linkedInFavicon,
        });
      }

      if (results.length === 0) {
        toast.info("No results found for this company");
      }

      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search for company");
    } finally {
      setSearchingDealId(null);
    }
  }, []);

  // Handle search result selection
  const handleSearchResultSelect = useCallback((dealId: string, result: CompanySearchResult, field: "website" | "linkedin") => {
    setDeals((prev) => {
      const updated = prev.map((deal) => {
        if (deal.id !== dealId) return deal;
        if (field === "website") {
          return { ...deal, website: result.website_url || deal.website };
        }
        return { ...deal, linkedin: result.linkedin_url || deal.linkedin };
      });

      // Close dropdowns only when both fields are filled
      const deal = updated.find(d => d.id === dealId);
      if (deal?.website && deal?.linkedin) {
        setSearchPopoverOpenId(null);
      }

      return updated;
    });
    toast.success(field === "website" ? "Website selected" : "LinkedIn selected");
  }, []);

  const validateWebsites = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    let valid = true;
    for (const deal of deals) {
      if (deal.website.trim()) {
        const error = getUrlValidationError(deal.website);
        if (error) {
          errors[deal.id] = error;
          valid = false;
        }
      }
    }
    setWebsiteErrors(errors);
    return valid;
  }, [deals]);

  const validateLinkedIns = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    let valid = true;
    for (const deal of deals) {
      if (deal.linkedin.trim()) {
        const error = getLinkedInUrlValidationError(deal.linkedin);
        if (error) {
          errors[deal.id] = error;
          valid = false;
        }
      }
    }
    setLinkedinErrors(errors);
    return valid;
  }, [deals]);

  const handleAddDeals = async () => {
    if (!activeWorkspaceId) {
      toast.error("No workspace selected. Please select a workspace.");
      return;
    }

    if (!user) {
      toast.error("User not authenticated. Please log in again.");
      return;
    }

    setIsLoading(true);
    setProgress(0);

    const validDeals = deals.filter((deal) => deal.name.trim() !== "");

    if (validDeals.length === 0) {
      toast.error("Please add at least one deal with a company name");
      setIsLoading(false);
      return;
    }

    if (!validateWebsites() || !validateLinkedIns()) {
      toast.error("Please fix invalid URLs before submitting");
      setIsLoading(false);
      return;
    }

    posthog.capture("bulk_add_started", {
      deal_count: validDeals.length,
      workspace_id: activeWorkspaceId,
    });

    try {
      for (let i = 0; i < validDeals.length; i += BATCH_SIZE) {
        const batch = validDeals.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (deal) => {
            const createCompanyResponse =
              await createDeal.createCompanyPitchbox({
                userId: user!.id,
                workspaceId: activeWorkspaceId!,
                companyName: deal.name,
                companyWebsite: deal.website,
              });

            if (createCompanyResponse.status === "error") {
              throw new Error(`Failed to create company: ${deal.name}`);
            }

            const companyId = createCompanyResponse.result._id;
            await createDeal.createOpportunityApplication(
              companyId,
              activeWorkspaceId!,
              deal.stage,
              deal.source
            );

            if (deal.linkedin.trim()) {
              try {
                await editDealInfo.editDealInfo({
                  workspaceId: activeWorkspaceId!,
                  companyId,
                  fieldName: "linkedIn",
                  fieldValue: deal.linkedin.trim(),
                });
              } catch {
                console.error(`Failed to add LinkedIn URL for: ${deal.name}`);
              }
            }
          })
        );

        setProgress(((i + batch.length) / validDeals.length) * 100);
      }

      posthog.capture("bulk_add_completed", {
        deal_count: validDeals.length,
        workspace_id: activeWorkspaceId,
      });

      toast.success("Deals added successfully!");
      fetchDeals(activeWorkspaceId);
      navigate("/deals");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to add deals. Please try again.";

      posthog.capture("bulk_add_failed", {
        deal_count: validDeals.length,
        workspace_id: activeWorkspaceId,
        error: errorMessage,
      });

      toast.error(getErrorMessage(error, errorMessage));
      console.error("Error adding deals:", error);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="shrink-0">
        <div className="flex h-16 items-end justify-between border-b border-border px-6 pb-3">
          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/deals")}
              className="mb-0.5 inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <h1 className="font-serif text-[32px] leading-[1.15] tracking-tight text-foreground">
              Bulk Add Deals
            </h1>
          </div>
        </div>
        <div className="border-b border-border bg-muted/30 px-6 py-2.5 text-xs text-muted-foreground">
          Add multiple deals at once. Fill in the company name (required),
          website, LinkedIn URL, stage and source for each deal.
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Mobile/Tablet View */}
        <div className="block lg:hidden space-y-4">
          {deals.map((deal) => (
            <Card key={deal.id} className="p-4 mb-4">
              <div className="space-y-4">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 flex gap-2">
                    <Input
                      value={deal.name}
                      onChange={(e) =>
                        updateDeal(deal.id, "name", e.target.value)
                      }
                      placeholder="Company Name*"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={searchingDealId === deal.id || !deal.name.trim()}
                      title="Search company info"
                      data-search-dropdown
                      onClick={() => handleCompanySearch(deal.id, deal.name)}
                    >
                      {searchingDealId === deal.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(deal.id)}
                    className="ml-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="relative">
                  <Input
                    value={deal.website}
                    onChange={(e) =>
                      updateDeal(deal.id, "website", e.target.value)
                    }
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val) {
                        const error = getUrlValidationError(val);
                        setWebsiteErrors((prev) => {
                          const next = { ...prev };
                          if (error) next[deal.id] = error;
                          else delete next[deal.id];
                          return next;
                        });
                      } else {
                        setWebsiteErrors((prev) => {
                          const next = { ...prev };
                          delete next[deal.id];
                          return next;
                        });
                      }
                    }}
                    placeholder="Website (https://)"
                    className={`w-full ${websiteErrors[deal.id] ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                  {websiteErrors[deal.id] && (
                    <p className="text-xs text-red-500 mt-1">{websiteErrors[deal.id]}</p>
                  )}
                  {searchingDealId === deal.id && !deal.website && (
                    <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center p-3">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">Searching...</span>
                      </div>
                    </div>
                  )}
                  {searchResults.some(r => r.source === "web") && searchPopoverOpenId === deal.id && !deal.website && (
                    <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                      <div className="max-h-[200px] overflow-y-auto">
                        {searchResults.filter(r => r.source === "web").map((result, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSearchResultSelect(deal.id, result, "website")}
                          >
                            <div className="flex items-center gap-2">
                              {result.favicon ? (
                                <img src={result.favicon} alt="" className="h-4 w-4 shrink-0 rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <Globe className="h-4 w-4 text-blue-500 shrink-0" />
                              )}
                              <span className="text-sm font-medium truncate">{result.company_name || deal.name}</span>
                            </div>
                            {result.website_url && (
                              <p className="text-xs text-muted-foreground truncate mt-1 ml-6">{result.website_url}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Input
                    value={deal.linkedin}
                    onChange={(e) =>
                      updateDeal(deal.id, "linkedin", e.target.value)
                    }
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val) {
                        const error = getLinkedInUrlValidationError(val);
                        setLinkedinErrors((prev) => {
                          const next = { ...prev };
                          if (error) next[deal.id] = error;
                          else delete next[deal.id];
                          return next;
                        });
                      } else {
                        setLinkedinErrors((prev) => {
                          const next = { ...prev };
                          delete next[deal.id];
                          return next;
                        });
                      }
                    }}
                    placeholder="LinkedIn URL"
                    className={`w-full ${linkedinErrors[deal.id] ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  />
                  {searchingDealId === deal.id && !deal.linkedin && (
                    <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center p-3">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">Searching...</span>
                      </div>
                    </div>
                  )}
                  {searchResults.some(r => r.source === "linkedin") && searchPopoverOpenId === deal.id && !deal.linkedin && (
                    <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                      <div className="max-h-[200px] overflow-y-auto">
                        {searchResults.filter(r => r.source === "linkedin").map((result, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSearchResultSelect(deal.id, result, "linkedin")}
                          >
                            <div className="flex items-center gap-2">
                              {result.favicon ? (
                                <img src={result.favicon} alt="" className="h-4 w-4 shrink-0 rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <Linkedin className="h-4 w-4 text-blue-600 shrink-0" />
                              )}
                              <span className="text-sm font-medium truncate">{result.company_name || deal.name}</span>
                            </div>
                            {result.linkedin_url && (
                              <p className="text-xs text-muted-foreground truncate mt-1 ml-6">{result.linkedin_url}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {linkedinErrors[deal.id] && (
                    <p className="text-xs text-red-500 mt-1">{linkedinErrors[deal.id]}</p>
                  )}
                </div>
                <Select
                  value={deal.stage}
                  onValueChange={(value) => updateDeal(deal.id, "stage", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stageOptions.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {stage}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={deal.source}
                  onValueChange={(value) =>
                    updateDeal(deal.id, "source", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceOptions.map((source) => (
                      <SelectItem key={source} value={source}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden lg:block border rounded-lg overflow-visible">
        <table className="w-full table-fixed">
          <thead className="bg-muted">
            <tr>
              <th className="p-4 text-left">Company Name*</th>
              <th className="p-4 text-left">Website</th>
              <th className="p-4 text-left">LinkedIn</th>
              <th className="p-4 text-left">Stage</th>
              <th className="p-4 text-left">Source</th>
              <th className="p-4 w-[50px]"></th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => (
              <tr key={deal.id} className="border-t align-top">
                <td className="p-4">
                  <div className="flex gap-1">
                    <Input
                      value={deal.name}
                      onChange={(e) =>
                        updateDeal(deal.id, "name", e.target.value)
                      }
                      placeholder="Enter company name"
                      className="w-full"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      disabled={searchingDealId === deal.id || !deal.name.trim()}
                      title="Search company info"
                      data-search-dropdown
                      onClick={() => handleCompanySearch(deal.id, deal.name)}
                    >
                      {searchingDealId === deal.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </td>
                <td className="p-4">
                  <div className="relative">
                    <Input
                      value={deal.website}
                      onChange={(e) =>
                        updateDeal(deal.id, "website", e.target.value)
                      }
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val) {
                          const error = getUrlValidationError(val);
                          setWebsiteErrors((prev) => {
                            const next = { ...prev };
                            if (error) next[deal.id] = error;
                            else delete next[deal.id];
                            return next;
                          });
                        } else {
                          setWebsiteErrors((prev) => {
                            const next = { ...prev };
                            delete next[deal.id];
                            return next;
                          });
                        }
                      }}
                      placeholder="https://"
                      className={`w-full ${websiteErrors[deal.id] ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                    {websiteErrors[deal.id] && (
                      <p className="text-xs text-red-500 mt-1">{websiteErrors[deal.id]}</p>
                    )}
                    {searchingDealId === deal.id && !deal.website && (
                      <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center p-3">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span className="text-sm text-muted-foreground">Searching...</span>
                        </div>
                      </div>
                    )}
                    {searchResults.some(r => r.source === "web") && searchPopoverOpenId === deal.id && !deal.website && (
                      <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="max-h-[200px] overflow-y-auto">
                          {searchResults.filter(r => r.source === "web").map((result, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSearchResultSelect(deal.id, result, "website")}
                            >
                              <div className="flex items-center gap-2">
                                {result.favicon ? (
                                  <img src={result.favicon} alt="" className="h-4 w-4 shrink-0 rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                  <Globe className="h-4 w-4 text-blue-500 shrink-0" />
                                )}
                                <span className="text-sm font-medium truncate">{result.company_name || deal.name}</span>
                              </div>
                              {result.website_url && (
                                <p className="text-xs text-muted-foreground truncate mt-1 ml-6">{result.website_url}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="relative">
                    <Input
                      value={deal.linkedin}
                      onChange={(e) =>
                        updateDeal(deal.id, "linkedin", e.target.value)
                      }
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val) {
                          const error = getLinkedInUrlValidationError(val);
                          setLinkedinErrors((prev) => {
                            const next = { ...prev };
                            if (error) next[deal.id] = error;
                            else delete next[deal.id];
                            return next;
                          });
                        } else {
                          setLinkedinErrors((prev) => {
                            const next = { ...prev };
                            delete next[deal.id];
                            return next;
                          });
                        }
                      }}
                      placeholder="linkedin.com/company/..."
                      className={`w-full ${linkedinErrors[deal.id] ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                    {linkedinErrors[deal.id] && (
                      <p className="text-xs text-red-500 mt-1">{linkedinErrors[deal.id]}</p>
                    )}
                    {searchingDealId === deal.id && !deal.linkedin && (
                      <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center p-3">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span className="text-sm text-muted-foreground">Searching...</span>
                        </div>
                      </div>
                    )}
                    {searchResults.some(r => r.source === "linkedin") && searchPopoverOpenId === deal.id && !deal.linkedin && (
                      <div data-search-dropdown className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="max-h-[200px] overflow-y-auto">
                          {searchResults.filter(r => r.source === "linkedin").map((result, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSearchResultSelect(deal.id, result, "linkedin")}
                            >
                              <div className="flex items-center gap-2">
                                {result.favicon ? (
                                  <img src={result.favicon} alt="" className="h-4 w-4 shrink-0 rounded-sm" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                  <Linkedin className="h-4 w-4 text-blue-600 shrink-0" />
                                )}
                                <span className="text-sm font-medium truncate">{result.company_name || deal.name}</span>
                              </div>
                              {result.linkedin_url && (
                                <p className="text-xs text-muted-foreground truncate mt-1 ml-6">{result.linkedin_url}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <Select
                    value={deal.stage}
                    onValueChange={(value) =>
                      updateDeal(deal.id, "stage", value)
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {stageOptions.map((stage) => (
                        <SelectItem key={stage} value={stage}>
                          {stage}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <Select
                    value={deal.source}
                    onValueChange={(value) =>
                      updateDeal(deal.id, "source", value)
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceOptions.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(deal.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

        <Button variant="outline" onClick={addNewRow} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Row
        </Button>
      </div>

      <div className="shrink-0 border-t border-border px-6 py-3 space-y-2">
        {isLoading && (
          <div className="space-y-2">
            <Progress value={progress} className="w-full" />
            <p className="text-sm text-muted-foreground text-center">
              Adding deals... {Math.round(progress)}%
            </p>
          </div>
        )}

        <Button
          className="w-full"
          onClick={handleAddDeals}
          disabled={isLoading || deals.every((deal) => !deal.name.trim())}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Adding Deals...
            </>
          ) : (
            "Add Deals"
          )}
        </Button>
      </div>
    </div>
  );
}
